/* eslint-disable max-lines */
import { FunnelIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { FC, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { defaultConfig } from "../../../hooks/useSearch";
import { IFilters } from "../../../interfaces/components/HeaderPage.types";
import { cx } from "../../../utils/classnames";
import Animation from "../../ui/Animation";
import Box from "../../ui/Box";
import ClickAwayListener from "../../ui/Clickaway";
import Checkbox from "../../ui/input/Checkbox";

const Filter: FC<IFilters> = ({ options, filterName, filterSelctor }) => {
  const [open, setOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { state } = useLocation();

  const selector = filterSelctor || defaultConfig.filterSelector;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const checked = event.target.checked;

    // Always work on a fresh params instance
    const params = new URLSearchParams(searchParams);

    if (checked) {
      params.set(selector, value);
    } else {
      params.delete(selector);
    }

    params.delete(defaultConfig.paginationSelector);
    setSearchParams(params, { replace: true, state });
  };

  const onClear = () => {
    const params = new URLSearchParams(searchParams);
    params.delete(selector);
    setSearchParams(params, { replace: true, state });
    setOpen(false);
  };

  const filterLength = Array.from(searchParams.entries()).filter(
    ([key]) => key === selector
  ).length;

  return (
    <div className="ml-3 md:ml-6">
      <ClickAwayListener onClickAway={() => setOpen(false)}>
        <div className="relative">
          <button
            onClick={() => setOpen((prev) => !prev)}
            className={cx([
              "flex justify-center items-center leading-5 rounded-lg whitespace-nowrap group",
              "ring-1 transition-shadow duration-200 hover:ring-gray-500 py-2 px-3",
              open ? "ring-primary" : "ring-blue-stroke"
            ])}
          >
            <FunnelIcon
              className={cx([
                "w-4 h-5 duration-500",
                filterLength
                  ? "text-primary"
                  : open
                  ? "text-black"
                  : "text-neutral group-hover:text-black duration-200"
              ])}
              {...(filterLength && {
                fill: "currentColor",
                fillRule: "evenodd"
              })}
            />
            <span
              className={cx([
                "md:inline hidden md:ml-2 ml-0",
                filterLength || open
                  ? "text-black"
                  : "text-neutral group-hover:text-black duration-200"
              ])}
            >
              {filterName}
            </span>
          </button>
          <Animation type="height" show={open} leaveInDom={!!options?.length}>
            <div
              className="absolute mt-2 right-0 z-50"
              style={{ minWidth: "calc(100% + 80px)" }}
            >
              <Box padding="py-1">
                {options?.map((el) => {
                  const value = el.id.toString();
                  const uniqueId = `${selector}-${value}`; // <- make IDs unique per filter
                  const checked =
                    searchParams.get(selector) === value || false;

                  return (
                    <Checkbox
                      key={`${el.label}_${uniqueId}`}
                      label={el.label}
                      name={`${selector}-${el.label.toLowerCase()}`} // also unique name
                      id={uniqueId}
                      value={value}
                      onChange={handleChange}
                      checked={checked}
                      inputClassName="px-3 py-2 hover:bg-gray-50"
                    />
                  );
                })}
                <div
                  className="border-t border-gray-200 px-3 py-2 flex justify-start items-center text-sm
              hover:bg-gray-50 cursor-pointer group"
                  onClick={onClear}
                >
                  <XMarkIcon
                    className="w-[18px] h-[18px] mr-2 text-neutral-300
                    group-hover:text-neutral duration-300
                  "
                    strokeWidth={2}
                  />
                  Clear
                </div>
              </Box>
            </div>
          </Animation>
        </div>
      </ClickAwayListener>
    </div>
  );
};

export default Filter;
